import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { useCallback, useTransition } from 'react'

interface QueryParamUpdateConfig {
  name: string
  value?: string
  deleteParam?: boolean
}

// Refer to https://nextjs.org/docs/app/api-reference/functions/use-search-params#updating-searchparams
export function useUpdateSearchParams(
  replace = true,
  scroll: boolean = true,
  enableTransition = false,
) {
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()

  const routerMethod = replace ? router.replace : router.push

  const [isPending, startTransition] = useTransition()

  const updateSearchParams = useCallback(
    (paramConfigs: QueryParamUpdateConfig | QueryParamUpdateConfig[]) => {
      const urlSearchParams = new URLSearchParams(searchParams.toString())
      const configs = Array.isArray(paramConfigs) ? paramConfigs : [paramConfigs]
      configs.forEach(({ name, value, deleteParam }) => {
        if (deleteParam) {
          urlSearchParams.delete(name)
        } else {
          urlSearchParams.set(name, value ?? '')
        }
      })
      const url = `${pathname}?${urlSearchParams.toString()}`
      if (enableTransition) {
        startTransition(() => {
          routerMethod(url, { scroll })
        })
      } else {
        routerMethod(url, { scroll })
      }
    },
    [searchParams, pathname, routerMethod, enableTransition, scroll],
  )

  return { updateSearchParams, isPending }
}
